import { Button, Typography } from "@mui/material";
import { CustomerCardWidget } from "@/widgets/customer-card-modal";
import { CLIENT_TYPE, CUSTOMER_ACTIONS } from "@/pages/customers/enums";
import { Permissions } from "@/components/CheckPermission/enum";
import { PermissionCheck } from "@/components/CheckPermission";
import { useEffect, useState } from "react";
import { getClientBalanceApi } from "@/services/api-service/customers/customers-api";
import { useGomakeAxios } from "@/hooks";
import { FONT_FAMILY } from "@/utils/font-family";
import { useTranslation } from "react-i18next";
import { useCustomerEdit } from "@/hooks/use-customer-edit";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

interface IProps {
  clientId: string;
  clientBalance?: number;
  isClickable?: boolean;
  clientType?: CLIENT_TYPE;
}

const ClientBalanceComponent = ({
  clientBalance,
  clientId,
  clientType = CLIENT_TYPE.CUSTOMER,
}: IProps) => {
  const { t } = useTranslation();
  const {
    showCustomerModal,
    customerForEdit,
    setCustomerForEdit,
    getCustomerForEdit,
    closeModal,
  } = useCustomerEdit();

  const { callApi } = useGomakeAxios();
  const { primaryColor, errorColor } = useGomakeTheme();

  const [balance, setBalance] = useState<number | undefined>(clientBalance);

  useEffect(() => {
    // Update balance when clientBalance prop changes
    if (clientBalance !== undefined) {
      setBalance(clientBalance);
    } else if (clientId) {
      // Only fetch if clientBalance is undefined (not provided) and we have a clientId
      // Note: 0 is a valid balance, so we only fetch when it's undefined
      getClientBalanceApi(
        callApi,
        (res) => {
          if (res.success) {
            setBalance(res.data);
          }
        },
        clientId,
      ).then();
    } else {
      // No clientId, reset balance
      setBalance(undefined);
    }
  }, [clientBalance, clientId, callApi]);

  // Determine if balance is negative (only if balance is defined)
  const isNegative = balance !== undefined && balance < 0;

  return (
    <>
      <PermissionCheck userPermission={Permissions.SHOW_CLIENT_BALANCE}>
        {balance !== undefined && balance !== null && (
          <Button
            onClick={() => getCustomerForEdit(clientId)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              py: "6px",
              borderRadius: "32px",
              textTransform: "none",
              boxShadow: "none",
              border: isNegative
                ? `1px solid var(--theme-balance-border-negative, ${errorColor(200)})`
                : `1px solid ${primaryColor(200)}`,
              backgroundColor: isNegative
                ? `var(--theme-balance-bg-color-negative, ${errorColor(100)})`
                : primaryColor(100),
              color: "inherit",
              minWidth: 0,
              maxWidth: "100%",
              "&:hover": {
                backgroundColor: isNegative
                  ? `var(--theme-balance-bg-color-negative, ${errorColor(200)})`
                  : primaryColor(50),
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                flexShrink: 0,
                ...FONT_FAMILY.Inter(500, 13),
              }}
            >
              {t("home.clientBalance")}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: isNegative
                  ? `var(--theme-balance-color-negative, ${errorColor(500)})`
                  : primaryColor(700),
                whiteSpace: "nowrap",
                ...FONT_FAMILY.Inter(600, 14),
              }}
            >
              {balance}
            </Typography>
          </Button>
        )}
      </PermissionCheck>
      <CustomerCardWidget
        customerAction={CUSTOMER_ACTIONS.Edit}
        typeClient={clientType}
        codeFlag={true}
        isgetAllCustomers={false}
        openModal={showCustomerModal}
        onClose={closeModal}
        customer={customerForEdit}
        setCustomer={setCustomerForEdit}
        selectedTabIndex={clientType === CLIENT_TYPE.SUPPLIER ? 5 : 6}
      />
    </>
  );
};

export { ClientBalanceComponent };
